import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { driverBillingBody, odometerBody } from '@pulsepoint/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { useAddOdometer, useDeleteOdometer, useDriverBilling, type DriverDetail } from '../../api/drivers';
import { usePermission } from '../../auth/permissions';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow, FormSection } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { formatDate } from '../../lib/format';
import { colours, radius } from '../../theme';

/** Enter in a panel inside the driver form runs the panel's own button, not "Save changes". */
const enterRuns = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
    e.preventDefault();
    fn();
  }
};

const today = () => new Date().toLocaleDateString('en-CA'); // yyyy-mm-dd, local

type OdoIn = z.input<typeof odometerBody>;
type OdoOut = z.output<typeof odometerBody>;

/** Odometer readings, newest first. Used to work out how far a car goes per kWh. */
export function OdometerPanel({ driver }: { driver: DriverDetail }) {
  const canEdit = usePermission('drivers.edit') && driver.isActive;
  const add = useAddOdometer();
  const remove = useDeleteOdometer();
  const toast = useToast();
  const [error, setError] = useState('');
  const form = useForm<OdoIn, unknown, OdoOut>({ resolver: zodResolver(odometerBody), defaultValues: { km: '', recordedOn: today() } as unknown as OdoIn });

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      await add.mutateAsync({ id: driver.id, ...v });
      form.reset({ km: '', recordedOn: today() } as unknown as OdoIn);
      toast('The reading was added.');
    } catch (e) {
      setError(applyApiError(e, form.setError, ['km', 'recordedOn']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset(), tab: true });

  return (
    <FormSection title="Odometer readings">
      {canEdit && (
        <Box role="group" aria-label="Add a reading" onKeyDown={enterRuns(() => void submit())} sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, alignItems: 'start' }}>
          <FormTextField control={form.control} name="recordedOn" label="Date" type="date" slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today() } }} />
          <FormTextField control={form.control} name="km" label="Reading (km)" slotProps={{ htmlInput: { inputMode: 'numeric' } }} />
          <Button startIcon={<Plus size={18} strokeWidth={1.75} />} onClick={() => void submit()} disabled={add.isPending} sx={{ mt: { sm: 0.5 } }}>
            Add
          </Button>
        </Box>
      )}
      {error && <Typography color="error" role="alert">{error}</Typography>}
      {driver.odometer.length === 0 ? (
        <Typography color="textSecondary">No readings yet.</Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px` }}>
          {driver.odometer.map((l, i) => (
            <Box component="li" key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderTop: i ? `1px solid ${colours.hairline}` : 0 }}>
              <Typography sx={{ width: 120 }}>{formatDate(l.recordedAt)}</Typography>
              <Typography className="num" sx={{ flex: 1, fontWeight: 500 }}>{l.km.toLocaleString('en-AU')} km</Typography>
              {i < driver.odometer.length - 1 && (
                <Typography variant="body2" color="textSecondary" className="num" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  +{(l.km - driver.odometer[i + 1]!.km).toLocaleString('en-AU')} km
                </Typography>
              )}
              {canEdit && (
                <Tooltip title="Delete this reading" describeChild>
                  <IconButton
                    size="small"
                    aria-label={`Delete the reading of ${formatDate(l.recordedAt)}`}
                    onClick={() => void remove.mutateAsync({ id: driver.id, logId: l.id }).then(() => toast('The reading was deleted.'))}
                  >
                    <Trash2 size={18} strokeWidth={1.75} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}
    </FormSection>
  );
}

type BillIn = z.input<typeof driverBillingBody>;
type BillOut = z.output<typeof driverBillingBody>;
const DAYS = Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Monthly billing for this driver, through Square (usually a home charger billed to them).
 * Admins set it up; managers can see it. Invoices themselves come later.
 */
export function BillingPanel({ driver }: { driver: DriverDetail }) {
  const canRun = usePermission('billing.run');
  const save = useDriverBilling();
  const toast = useToast();
  const [error, setError] = useState('');
  const b = driver.billing ?? { isBillable: false, dayOfMonth: 1, stripeCustomerId: null, stripeCustomerEmail: null, lastBilledYear: null, lastBilledMonth: null };
  const form = useForm<BillIn, unknown, BillOut>({
    resolver: zodResolver(driverBillingBody),
    values: { isBillable: b.isBillable, dayOfMonth: String(b.dayOfMonth), stripeCustomerEmail: b.stripeCustomerEmail ?? '' } as unknown as BillIn,
    resetOptions: { keepDirtyValues: true },
    disabled: !canRun,
  });
  const billable = form.watch('isBillable');

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      await save.mutateAsync({ id: driver.id, ...v });
      form.reset(undefined, { keepValues: true });
      toast(v.isBillable ? `${driver.name} will be billed on day ${v.dayOfMonth} of each month.` : `${driver.name} won't be billed.`);
    } catch (e) {
      setError(applyApiError(e, form.setError, ['isBillable', 'dayOfMonth', 'stripeCustomerEmail']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset(), tab: true });

  return (
    <Box role="group" aria-label="Billing" onKeyDown={enterRuns(() => void submit())} sx={{ display: 'grid', gap: 3 }}>
      <Controller
        control={form.control}
        name="isBillable"
        render={({ field }) => (
          <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={field.disabled} />} label="Bill this driver every month" />
        )}
      />
      <FieldRow columns="1fr 2fr">
        <FormSelect control={form.control} name="dayOfMonth" label="Billing day" options={DAYS} disabled={!canRun || !billable} helperText="Day 1 to 28 of each month" />
        <FormTextField control={form.control} name="stripeCustomerEmail" label="Invoice email" type="email" disabled={!canRun || !billable} placeholder={driver.email} helperText="Blank: their own email" />
      </FieldRow>
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Typography variant="body2" color="textSecondary">Billing customer: {b.stripeCustomerId ?? 'created with the first invoice'}</Typography>
        <Typography variant="body2" color="textSecondary">
          Last billed: {b.lastBilledYear && b.lastBilledMonth ? `${MONTHS[b.lastBilledMonth - 1]} ${b.lastBilledYear}` : 'never'}
        </Typography>
      </Box>
      <Alert severity="info">Monthly invoices aren't built yet (they'll go through Square). This sets who gets billed.</Alert>
      {error && <Typography color="error" role="alert">{error}</Typography>}
      {canRun && (
        <Box>
          <Button variant="contained" onClick={() => void submit()} disabled={save.isPending || !form.formState.isDirty}>
            Save billing
          </Button>
        </Box>
      )}
    </Box>
  );
}
